import React, { useEffect, useState } from 'react';
import {
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  TableBody,
  Typography,
  useTheme,
  Box,
  IconButton,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { CustomTableHead, CustomTableHeadCell, CustomTableRow } from '../../components/@extended/CustomTable';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { useGetSiteMembersQuery } from '../../store/slices/apiSlice';
import CustomCard from '../../components/@extended/CustomCard';
import CustomButton from '../../components/@extended/CustomButton';
import UserManagementDialog from './UserManagementDialog';
import AddMember from '../createTrial/siteDetails/AddMember';
import { useDispatch } from 'react-redux';
import { openMessage } from '../../store/slices/showMessageSlice';

const siteUserColumns = [
  {
    id: 'userName',
    label: 'Name',
    style: {
      minWidth: 150,
      textAlign: 'left',
    },
    align: 'left',
  },
  {
    id: 'email',
    label: 'Email',
    style: {
      minWidth: 200,
      textAlign: 'left',
    },
    align: 'left',
  },
  {
    id: 'contactNumber',
    label: 'Contact Number',
    style: {
      minWidth: 150,
      textAlign: 'left',
    },
    align: 'left',
  },
  {
    id: 'createdAt',
    label: 'Created Date',
    style: {
      minWidth: 150,
      textAlign: 'center',
    },
    align: 'center',
  },
  {
    id: 'actions',
    label: 'Actions',
    align: 'center',
    style: { minWidth: 100 },
  },
];

const SiteUsersTable = ({ siteUsers: initialSiteUsers, isLoading: initialIsLoading, sponsorId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { siteId } = useParams();
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [openAddUser, setOpenAddUser] = useState(false);

  // Use query if siteUsers not provided
  const { data: siteUsersFromQuery, isLoading: isLoadingFromQuery, refetch } = useGetSiteMembersQuery(
    { sponsorId, siteId },
    { skip: !!initialSiteUsers }
  );

  const siteUsers = initialSiteUsers || siteUsersFromQuery || [];
  const isLoading = initialIsLoading || isLoadingFromQuery;

  useEffect(() => {
    if (!selectedUser) return;
    const user = siteUsersFromQuery?.find((user) => user?.id === selectedUser?.id);
    if (user) {
      setSelectedUser(user);
    }
  }, [selectedUser, siteUsersFromQuery, sponsorId, siteId]);

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleUserUpdate = () => {
    if (refetch) {
      refetch();
    }
  };

  const handleCloseAddUser = () => {
    setOpenAddUser(false);
  };

  const handleAddUserSuccess = () => {
    if (refetch) {
      refetch();
    }
    setOpenAddUser(false);
  };

  const openMessageNotification = ({ message, type }) => {
    dispatch(openMessage({ messageSeverity: type, message }));
  };

  if (isLoading) {
    return <></>;
  }

  return (
    <Box>
      <CustomCard
        title={
          <Typography variant="h6" fontWeight={600} color={theme.palette.grey[800]}>
            Site Users
          </Typography>
        }
        action={
          <Box sx={{ display: 'flex', columnGap: 2, alignItems: 'center' }}>
            <CustomButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddUser(true)}
            >
              Create New User
            </CustomButton>
          </Box>
        }
      >
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="sticky table">
            <CustomTableHead>
              <TableRow>
                {siteUserColumns.map((column) => (
                  <CustomTableHeadCell key={column.id} align={column.align} sx={{ ...column.style }}>
                    {column.label}
                  </CustomTableHeadCell>
                ))}
              </TableRow>
            </CustomTableHead>
            <TableBody>
              {siteUsers && siteUsers.length > 0 ? (
                siteUsers.map((row) => (
                  <CustomTableRow key={row?.id} hover role="checkbox" tabIndex={-1}>
                    <TableCell>
                      {row?.firstName} {row?.lastName}
                    </TableCell>
                    <TableCell>{row?.email}</TableCell>
                    <TableCell>{row?.contactNumber}</TableCell>
                    <TableCell>{moment(row?.createdAt).format('DD/MM/YYYY')}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        aria-label="manage user"
                        onClick={() => handleUserClick(row)}
                        sx={{
                          color: theme.palette.grey[600],
                          '&:hover': {
                            backgroundColor: theme.palette.grey[100],
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </CustomTableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={siteUserColumns.length}>
                    <Typography variant="subtitle1" color="initial">
                      No Users Found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <UserManagementDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          user={selectedUser}
          sponsorId={sponsorId}
          onUserUpdate={handleUserUpdate}
        />
      </CustomCard>
      <AddMember
        siteId={siteId}
        sponsorId={sponsorId}
        onAddMember={handleAddUserSuccess}
        handleClose={handleCloseAddUser}
        open={openAddUser}
      />
    </Box>
  );
};

export default SiteUsersTable;

